import React, { useState } from 'react';
import { Typography, SectionHeader } from '../components/ui/Typography';
import { SongCardSkeleton, AlbumCardSkeleton, ArtistCardSkeleton } from '../components/ui/SkeletonLoader';
import { AccessibilitySettings } from '../components/ui/AccessibilitySettings';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { VoiceBankIcon } from '../components/ui/VSynthIcons';
import { useAccessibility, useScreenReader } from '../context/AccessibilityContext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';

// Advanced Particle System
interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
  trail: Array<{x: number, y: number, alpha: number}>;
}

// Morphing shape component
const MorphingShape = ({ isActive, delay = 0 }: { isActive: boolean; delay?: number }) => {
  return (
    <div className={`absolute transition-all duration-[3s] ease-in-out transform ${isActive ? 'scale-100 opacity-100' : 'scale-50 opacity-30'}`}
         style={{ animationDelay: `${delay}ms` }}>
      <div className="w-32 h-32 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-naia-pink to-cyan-accent rounded-full animate-pulse-glow blur-sm"></div>
        <div className="absolute inset-2 bg-gradient-to-l from-cyan-accent to-soft-violet rounded-full animate-float"></div>
        <div className="absolute inset-4 bg-gradient-to-tr from-soft-violet to-naia-pink rounded-full animate-float-delayed"></div>
      </div>
    </div>
  );
};

// 3D Card Effect Component
const Card3D = ({ children, className = '', index = 0 }: { children: React.ReactNode; className?: string; index?: number }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`
        group relative transform-gpu transition-all duration-700 ease-out-quart
        ${isHovered ? 'scale-105 rotate-1' : 'scale-100 rotate-0'}
        ${className}
      `}
      style={{
        perspective: '1000px',
        animationDelay: `${index * 100}ms`
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Holographic background */}
      <div className="absolute -inset-1 bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet rounded-3xl blur opacity-20 group-hover:opacity-60 transition-all duration-700 animate-gradient-xy"></div>
      
      {/* Main card */}
      <div className={`
        relative bg-white/5 backdrop-blur-2xl rounded-3xl border border-white/10
        transform-gpu transition-all duration-700 ease-out-quart
        ${isHovered ? 'shadow-multi-glow border-white/30' : 'shadow-elevation-2'}
        group-hover:bg-white/10
      `}>
        {children}
      </div>
      
      {/* Animated border light */}
      <div className="absolute inset-0 rounded-3xl border-2 border-transparent bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet bg-clip-border opacity-0 group-hover:opacity-50 transition-all duration-700 animate-gradient-x"></div>
    </div>
  );
};

// Floating Icon Animation
const FloatingIcon = ({ icon: Icon, delay = 0, amplitude = 10 }: { icon: any; delay?: number; amplitude?: number }) => {
  return (
    <div 
      className="text-4xl text-naia-pink opacity-20 animate-float"
      style={{
        animationDelay: `${delay}s`,
        animationDuration: `${3 + Math.random() * 2}s`,
        transform: `translateY(${amplitude}px)`
      }}
    >
      <Icon />
    </div>
  );
};

export function UIShowcase() {
  const [activeDemo, setActiveDemo] = useState<string>('showcase');
  const [particles, setParticles] = useState<Particle[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMouseMoving, setIsMouseMoving] = useState(false);
  const [beautyScore, setBeautyScore] = useState(1);
  const [showSkeletons, setShowSkeletons] = useState(false);
  const [particleExplosion, setParticleExplosion] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const mouseTimeoutRef = useRef<NodeJS.Timeout>();
  
  const { state } = useAccessibility();
  const { announceAction } = useScreenReader();

  // Advanced particle system
  const createParticle = useCallback((x: number, y: number, count = 1): Particle[] => {
    const colors = ['#FF4D8C', '#4DE8FF', '#9C6B98', '#FFFFFF'];
    return Array.from({ length: count }, (_, i) => ({
      id: Date.now() + i,
      x,
      y,
      vx: (Math.random() - 0.5) * 8,
      vy: (Math.random() - 0.5) * 8,
      size: Math.random() * 4 + 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 100,
      maxLife: 100,
      trail: []
    }));
  }, []);

  const createExplosion = useCallback((x: number, y: number) => {
    const newParticles = createParticle(x, y, 30);
    setParticles(prev => [...prev, ...newParticles]);
    setParticleExplosion(true);
    setTimeout(() => setParticleExplosion(false), 200);
  }, [createParticle]);

  // Mouse tracking with trail
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        setMousePosition({ x, y });
        setIsMouseMoving(true);
        
        // Create subtle particles on movement
        if (Math.random() < 0.1) {
          const newParticles = createParticle(x, y, 2);
          setParticles(prev => [...prev, ...newParticles]);
        }
        
        clearTimeout(mouseTimeoutRef.current);
        mouseTimeoutRef.current = setTimeout(() => {
          setIsMouseMoving(false);
        }, 150);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(mouseTimeoutRef.current);
    };
  }, [createParticle]);

  // Particle animation loop
  useEffect(() => {
    const animate = () => {
      setParticles(prev => prev
        .map(particle => {
          // Add current position to trail
          particle.trail.push({ x: particle.x, y: particle.y, alpha: particle.life / particle.maxLife });
          if (particle.trail.length > 10) particle.trail.shift();
          
          // Update position
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vy += 0.1; // gravity
          particle.vx *= 0.99; // friction
          particle.life -= 1;
          
          return particle;
        })
        .filter(particle => particle.life > 0)
      );
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Gradual beauty score increase
  useEffect(() => {
    const interval = setInterval(() => {
      setBeautyScore(prev => {
        if (prev < 11) {
          return Math.min(11, prev + 0.1);
        }
        return prev;
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, []);

  const demoSections = [
    { 
      id: 'showcase', 
      label: 'Epic Showcase', 
      icon: <AutoAwesomeIcon />,
      gradient: 'from-naia-pink to-cyan-accent'
    },
    { 
      id: 'typography', 
      label: 'Text Magic', 
      icon: <FormatPaintIcon />,
      gradient: 'from-cyan-accent to-soft-violet'
    },
    { 
      id: 'buttons', 
      label: 'Button Universe', 
      icon: <RocketLaunchIcon />,
      gradient: 'from-soft-violet to-naia-pink'
    },
    { 
      id: 'effects', 
      label: 'Visual Symphony', 
      icon: <SparklesIcon />,
      gradient: 'from-naia-pink via-cyan-accent to-soft-violet'
    },
    { 
      id: 'interactions', 
      label: 'Micro Cosmos', 
      icon: <PaletteIcon />,
      gradient: 'from-cyan-accent to-naia-pink'
    }
  ];

  const handleExplosiveClick = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      createExplosion(x, y);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-true-black via-deep-night to-surface-dark relative overflow-hidden"
      onClick={handleExplosiveClick}
    >
      {/* Advanced Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Animated Background Shapes */}
        <div className="absolute top-1/4 left-1/6 opacity-10">
          <MorphingShape isActive={true} delay={0} />
        </div>
        <div className="absolute bottom-1/3 right-1/5 opacity-15">
          <MorphingShape isActive={true} delay={1000} />
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-8">
          <MorphingShape isActive={true} delay={2000} />
        </div>
        
        {/* Dynamic Grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-12 grid-rows-12 h-full w-full">
            {Array.from({ length: 144 }, (_, i) => (
              <div 
                key={i} 
                className="border border-white/10 animate-pulse"
                style={{ 
                  animationDelay: `${i * 50}ms`,
                  animationDuration: '4s'
                }}
              />
            ))}
          </div>
        </div>
        
        {/* Floating Icons */}
        <div className="absolute top-20 left-20">
          <FloatingIcon icon={DigitalWaveIcon} delay={0} />
        </div>
        <div className="absolute top-40 right-32">
          <FloatingIcon icon={VocalSynthIcon} delay={1} />
        </div>
        <div className="absolute bottom-40 left-40">
          <FloatingIcon icon={SynthKeyboardIcon} delay={2} />
        </div>
        <div className="absolute bottom-20 right-20">
          <FloatingIcon icon={VirtualSingerIcon} delay={3} />
        </div>
      </div>

      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-20"
        style={{ 
          background: 'transparent',
          mixBlendMode: 'screen'
        }}
      />

      {/* Particle Rendering */}
      <div className="fixed inset-0 pointer-events-none z-20">
        {particles.map(particle => (
          <div key={particle.id}>
            {/* Particle trail */}
            {particle.trail.map((point, index) => (
              <div
                key={index}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  left: point.x,
                  top: point.y,
                  backgroundColor: particle.color,
                  opacity: point.alpha * 0.5,
                  transform: `scale(${point.alpha})`
                }}
              />
            ))}
            {/* Main particle */}
            <div
              className="absolute rounded-full animate-pulse-glow"
              style={{
                left: particle.x,
                top: particle.y,
                width: particle.size,
                height: particle.size,
                backgroundColor: particle.color,
                opacity: particle.life / particle.maxLife,
                boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`
              }}
            />
          </div>
        ))}
      </div>

      {/* Mouse Cursor Effect */}
      {isMouseMoving && (
        <div 
          className="fixed w-32 h-32 rounded-full pointer-events-none z-30 transition-all duration-300"
          style={{
            left: mousePosition.x - 64,
            top: mousePosition.y - 64,
            background: 'radial-gradient(circle, rgba(255,77,140,0.2) 0%, rgba(77,232,255,0.1) 50%, transparent 70%)',
            filter: 'blur(20px)',
            transform: `scale(${particleExplosion ? 2 : 1})`
          }}
        />
      )}

      {/* Ultra Modern Navigation */}
      <nav className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="flex items-center space-x-1 bg-black/20 backdrop-blur-3xl rounded-3xl p-3 border border-white/20 shadow-multi-glow">
          {demoSections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => {
                setActiveDemo(section.id);
                announceAction(`Switched to ${section.label}`);
                createExplosion(mousePosition.x, mousePosition.y);
              }}
              className={`
                group relative px-6 py-3 rounded-2xl transition-all duration-500 flex items-center space-x-3 overflow-hidden
                ${activeDemo === section.id 
                  ? `bg-gradient-to-r ${section.gradient} text-white shadow-naia-intense scale-110` 
                  : 'text-slate-grey hover:text-white hover:bg-white/10 hover:scale-105'
                }
              `}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Button background animation */}
              <div className={`absolute inset-0 bg-gradient-to-r ${section.gradient} opacity-0 group-hover:opacity-20 transition-all duration-500 animate-gradient-x`}></div>
              
              <span className="relative z-10 text-xl transition-transform duration-300 group-hover:rotate-12">
                {section.icon}
              </span>
              <span className="relative z-10 font-semibold text-sm tracking-wider uppercase">
                {section.label}
              </span>
              
              {activeDemo === section.id && (
                <div className="absolute inset-0 bg-white/20 rounded-2xl animate-pulse"></div>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-40 pb-16 px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          
          {/* Epic Hero Section */}
          <div className="text-center mb-20 space-y-8 relative">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-naia-pink/10 via-cyan-accent/10 to-soft-violet/10 rounded-full blur-3xl animate-pulse-glow"></div>
            
            <div className="relative z-10">
              <div
                className="animate-slide-in-bottom text-8xl font-black tracking-tighter mb-6"
              >
                <span
                  className="text-transparent bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet bg-clip-text animate-gradient-xy"
                >
                  NAIA UI UNIVERSE
                </span>
              </div>
              
              <div className="max-w-3xl mx-auto animate-fade-in text-2xl leading-relaxed text-text-secondary">
                Experience the most impossibly beautiful and animated UI components ever created for VSynth music platforms
              </div>
            </div>
            
            {/* Interactive Stats */}
            <div className="flex justify-center items-center space-x-12 mt-12 relative z-10">
              <Card3D className="p-6" index={0}>
                <div className="text-center">
                  <div className="text-6xl font-black text-transparent bg-gradient-to-r from-naia-pink to-cyan-accent bg-clip-text animate-pulse-glow mb-2">
                    {particles.length}
                  </div>
                  <div className="text-sm text-slate-grey uppercase tracking-widest">Active Particles</div>
                </div>
              </Card3D>
              
              <Card3D className="p-6" index={1}>
                <div className="text-center">
                  <div className="text-6xl font-black text-transparent bg-gradient-to-r from-cyan-accent to-soft-violet bg-clip-text animate-bounce">
                    {beautyScore.toFixed(1)}/10
                  </div>
                  <div className="text-sm text-slate-grey uppercase tracking-widest">Beauty Score</div>
                </div>
              </Card3D>
              
              <Card3D className="p-6" index={2}>
                <div className="text-center">
                  <div className="text-6xl font-black text-transparent bg-gradient-to-r from-soft-violet to-naia-pink bg-clip-text animate-float">
                    ∞
                  </div>
                  <div className="text-sm text-slate-grey uppercase tracking-widest">Amazement Level</div>
                </div>
              </Card3D>
            </div>
          </div>

          {/* Dynamic Content Based on Active Demo */}
          <div className="transition-all duration-1000 ease-out-quart">
            
            {activeDemo === 'showcase' && (
              <div className="space-y-16 animate-fade-in">
                <Typography variant="h2" className="text-center text-5xl font-black text-transparent bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet bg-clip-text animate-gradient-x">
                  THE ULTIMATE UI SHOWCASE
                </Typography>
                
                {/* Feature Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[
                    {
                      title: "Particle Physics",
                      description: "Advanced particle systems with realistic physics, trails, and interactive explosions",
                      icon: <AutoAwesomeIcon className="text-6xl" />,
                      gradient: "from-naia-pink to-cyan-accent"
                    },
                    {
                      title: "3D Transformations",
                      description: "Cards and elements that respond with smooth 3D transforms and perspective effects",
                      icon: <BrushIcon className="text-6xl" />,
                      gradient: "from-cyan-accent to-soft-violet"
                    },
                    {
                      title: "Morphing Gradients",
                      description: "Dynamic gradients that shift, morph, and animate across multiple dimensions",
                      icon: <PaletteIcon className="text-6xl" />,
                      gradient: "from-soft-violet to-naia-pink"
                    },
                    {
                      title: "Micro-interactions",
                      description: "Every element responds with delightful animations and visual feedback",
                      icon: <SparklesIcon className="text-6xl" />,
                      gradient: "from-naia-pink via-cyan-accent to-soft-violet"
                    },
                    {
                      title: "Real-time Effects",
                      description: "Mouse tracking, hover states, and dynamic lighting effects in real-time",
                      icon: <RocketLaunchIcon className="text-6xl" />,
                      gradient: "from-cyan-accent to-naia-pink"
                    },
                    {
                      title: "Accessibility Magic",
                      description: "Beautiful animations that respect user preferences and accessibility needs",
                      icon: <VoiceBankIcon className="text-6xl" />,
                      gradient: "from-soft-violet via-naia-pink to-cyan-accent"
                    }
                  ].map((feature, index) => (
                    <Card3D key={feature.title} className="p-8 h-full" index={index}>
                      <div className="text-center space-y-6">
                        <div className={`text-transparent bg-gradient-to-r ${feature.gradient} bg-clip-text animate-float`}
                             style={{ animationDelay: `${index * 200}ms` }}>
                          {feature.icon}
                        </div>
                        <Typography variant="h4" className="font-bold text-glow-multi">
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" color="secondary" className="leading-relaxed">
                          {feature.description}
                        </Typography>
                      </div>
                    </Card3D>
                  ))}
                </div>
                
                {/* Call to Action */}
                <div className="text-center space-y-8">
                  <button
                    onClick={handleExplosiveClick}
                    className="group relative px-16 py-6 bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet rounded-full font-black text-white text-2xl overflow-hidden transition-all duration-700 hover:scale-110 hover:shadow-multi-glow transform-gpu"
                  >
                    <span className="relative z-10 uppercase tracking-wider">Create Particle Explosion</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent via-soft-violet to-naia-pink opacity-0 group-hover:opacity-100 transition-all duration-500 animate-gradient-x"></div>
                    <div className="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-100 transition-transform duration-500"></div>
                  </button>
                  
                  <Typography variant="body2" color="secondary" className="animate-pulse">
                    Click anywhere for magical particle explosions ✨
                  </Typography>
                </div>
              </div>
            )}

            {activeDemo === 'typography' && (
              <div className="space-y-16 animate-fade-in">
                <Typography variant="h2" className="text-center text-5xl font-black text-glow-multi">
                  TYPOGRAPHY MAGIC LABORATORY
                </Typography>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {/* Gradient Text Showcase */}
                  <Card3D className="p-8" index={0}>
                    <Typography variant="h3" className="mb-6 text-glow-naia">
                      Gradient Wizardry
                    </Typography>
                    <div className="space-y-4">
                      <Typography 
                        variant="display2" 
                        className="text-transparent bg-gradient-to-r from-naia-pink to-cyan-accent bg-clip-text animate-gradient-x"
                      >
                        MAGICAL
                      </Typography>
                      <Typography 
                        variant="display1" 
                        className="text-transparent bg-gradient-to-r from-cyan-accent via-soft-violet to-naia-pink bg-clip-text animate-gradient-xy"
                      >
                        VSYNTH
                      </Typography>
                    </div>
                  </Card3D>

                  {/* Animated Text Effects */}
                  <Card3D className="p-8" index={1}>
                    <Typography variant="h3" className="mb-6 text-glow-cyan">
                      Animation Effects
                    </Typography>
                    <div className="space-y-4">
                      <Typography variant="h2" className="text-naia-pink animate-pulse-glow">
                        Pulsing Glow
                      </Typography>
                      <Typography variant="h2" className="text-cyan-accent animate-float">
                        Floating Text
                      </Typography>
                      <Typography variant="h2" className="text-soft-violet animate-bounce">
                        Bouncy Fun
                      </Typography>
                    </div>
                  </Card3D>

                  {/* Interactive Typography */}
                  <Card3D className="p-8" index={2}>
                    <Typography variant="h3" className="mb-6 text-glow-multi">
                      Interactive Magic
                    </Typography>
                    <div className="space-y-4">
                      <button 
                        onClick={handleExplosiveClick}
                        className="block w-full text-left group"
                      >
                        <Typography variant="h4" className="text-white group-hover:text-glow-naia transition-all duration-300 group-hover:scale-110 transform-gpu cursor-pointer">
                          Hover & Click Me! ✨
                        </Typography>
                      </button>
                      <Typography variant="body1" className="text-balance">
                        This text responds to your interactions with beautiful animations and visual feedback.
                      </Typography>
                    </div>
                  </Card3D>
                </div>
              </div>
            )}

            {activeDemo === 'buttons' && (
              <div className="space-y-16 animate-fade-in">
                <Typography variant="h2" className="text-center text-5xl font-black text-glow-multi">
                  BUTTON UNIVERSE COLLECTION
                </Typography>
                
                {/* Button Showcase Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {/* Gradient Morphing Button */}
                  <Card3D className="p-6" index={0}>
                    <Button 
                      variant="gradient" 
                      size="lg" 
                      className="w-full animate-gradient-xy hover:scale-110 transform-gpu transition-all duration-500"
                      onClick={handleExplosiveClick}
                    >
                      <PlayArrowIcon className="mr-2" />
                      Play Magic
                    </Button>
                  </Card3D>

                  {/* Pulsing Love Button */}
                  <Card3D className="p-6" index={1}>
                    <Button 
                      variant="primary" 
                      size="lg" 
                      className="w-full animate-pulse-glow hover:animate-bounce transform-gpu transition-all duration-500"
                      onClick={handleExplosiveClick}
                    >
                      <FavoriteIcon className="mr-2" />
                      Love It
                    </Button>
                  </Card3D>

                  {/* Floating Ghost Button */}
                  <Card3D className="p-6" index={2}>
                    <Button 
                      variant="ghost" 
                      size="lg" 
                      className="w-full border-2 border-cyan-accent/50 hover:shadow-cyan-intense hover:animate-float transform-gpu transition-all duration-500"
                      onClick={handleExplosiveClick}
                    >
                      <ShareIcon className="mr-2" />
                      Share
                    </Button>
                  </Card3D>

                  {/* 3D Transform Button */}
                  <Card3D className="p-6" index={3}>
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="w-full hover:scale-110 hover:rotate-3 hover:shadow-multi-glow transform-gpu transition-all duration-500"
                      onClick={handleExplosiveClick}
                    >
                      <SearchIcon className="mr-2" />
                      Discover
                    </Button>
                  </Card3D>
                </div>

                {/* Special Ultra Buttons */}
                <div className="flex flex-wrap justify-center gap-8">
                  {/* Ultra Gradient Button */}
                  <button
                    onClick={handleExplosiveClick}
                    className="group relative px-12 py-6 bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet rounded-3xl font-black text-white text-xl overflow-hidden transform-gpu transition-all duration-700 hover:scale-110 hover:shadow-multi-glow animate-gradient-xy"
                  >
                    <span className="relative z-10 uppercase tracking-wider">Ultra Particle Creator</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-soft-violet via-naia-pink to-cyan-accent opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                    <div className="absolute -inset-1 bg-gradient-to-r from-naia-pink to-cyan-accent rounded-3xl blur opacity-30 group-hover:opacity-70 transition-all duration-500"></div>
                  </button>
                  
                  {/* Skeleton Toggle Button */}
                  <button
                    onClick={() => setShowSkeletons(!showSkeletons)}
                    className="px-12 py-6 bg-black/30 backdrop-blur-3xl border-2 border-white/30 rounded-3xl font-black text-white hover:bg-white/20 transform-gpu transition-all duration-500 hover:scale-105 hover:shadow-elevation-3 hover:border-white/50"
                  >
                    <span className="uppercase tracking-wider">
                      {showSkeletons ? 'Hide' : 'Show'} Loading States
                    </span>
                  </button>
                </div>
              </div>
            )}

            {activeDemo === 'effects' && (
              <div className="space-y-16 animate-fade-in">
                <Typography variant="h2" className="text-center text-5xl font-black text-glow-multi">
                  VISUAL SYMPHONY GALLERY
                </Typography>
                
                {/* Effects Showcase */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {/* Holographic Card */}
                  <div className="relative group">
                    <div className="absolute -inset-2 bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet rounded-3xl blur-lg opacity-30 group-hover:opacity-80 transition-all duration-1000 animate-gradient-xy"></div>
                    <div className="relative bg-deep-night/80 backdrop-blur-3xl rounded-3xl p-8 border border-white/20 group-hover:border-white/50 transition-all duration-500">
                      <Typography variant="h4" className="mb-4 text-glow-naia">
                        Holographic Glow
                      </Typography>
                      <Typography variant="body1" color="secondary">
                        Experience the future with holographic glow effects that respond to your interactions
                      </Typography>
                    </div>
                  </div>

                  {/* Morphing Glass Card */}
                  <Card3D className="p-8 backdrop-blur-3xl bg-white/5 border-cyan-accent/30 shadow-cyan-glow hover:shadow-cyan-intense" index={1}>
                    <Typography variant="h4" className="mb-4 text-glow-cyan">
                      Morphing Glass
                    </Typography>
                    <Typography variant="body1" color="secondary">
                      Beautiful glass morphism effects with dynamic shadows and smooth color transitions
                    </Typography>
                  </Card3D>

                  {/* Floating Gradient Card */}
                  <div className="bg-gradient-to-br from-soft-violet/20 via-naia-pink/20 to-cyan-accent/20 backdrop-blur-3xl rounded-3xl p-8 border border-white/20 hover:border-white/40 transition-all duration-700 animate-float-gentle hover:scale-105 transform-gpu">
                    <Typography variant="h4" className="mb-4 text-glow-multi">
                      Floating Dreams
                    </Typography>
                    <Typography variant="body1" color="secondary">
                      Gentle floating animations combined with multi-colored glow effects
                    </Typography>
                  </div>
                </div>

                {/* Interactive Effect Demos */}
                <div className="text-center space-y-8">
                  <Typography variant="h3" className="text-glow-multi">
                    Interactive Effect Laboratory
                  </Typography>
                  
                  <div className="flex flex-wrap justify-center gap-6">
                    <button
                      onClick={(e) => {
                        handleExplosiveClick(e);
                        createExplosion(mousePosition.x, mousePosition.y);
                      }}
                      className="px-8 py-4 bg-gradient-to-r from-naia-pink via-soft-violet to-cyan-accent rounded-2xl font-bold text-white text-lg shadow-multi-glow hover:shadow-naia-intense transition-all duration-500 hover:scale-110 animate-gradient-shift transform-gpu"
                    >
                      🌟 Particle Explosion 🌟
                    </button>
                    
                    <button
                      onClick={handleExplosiveClick}
                      className="px-8 py-4 bg-black/40 backdrop-blur-2xl border-2 border-cyan-accent/50 rounded-2xl font-bold text-white hover:bg-cyan-accent/20 transition-all duration-500 hover:scale-105 hover:border-cyan-accent"
                    >
                      ⚡ Lightning Strike ⚡
                    </button>
                    
                    <button
                      onClick={handleExplosiveClick}
                      className="px-8 py-4 bg-white/10 backdrop-blur-2xl border-2 border-soft-violet/50 rounded-2xl font-bold text-white hover:bg-soft-violet/20 transition-all duration-500 hover:scale-105 hover:border-soft-violet animate-pulse-glow"
                    >
                      ✨ Magic Sparkles ✨
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeDemo === 'interactions' && (
              <div className="space-y-16 animate-fade-in">
                <Typography variant="h2" className="text-center text-5xl font-black text-glow-multi">
                  MICRO-INTERACTION COSMOS
                </Typography>
                
                {/* Micro-interactions Showcase */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {/* Hover Effects */}
                  <Card3D className="p-8" index={0}>
                    <Typography variant="h4" className="mb-6 text-glow-naia">
                      Hover Magic
                    </Typography>
                    <div className="space-y-4">
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10 hover:border-naia-pink/50 hover:bg-white/10 hover:shadow-naia-glow transition-all duration-300 cursor-pointer">
                        <Typography variant="body2">Hover for glow effect</Typography>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10 hover:border-cyan-accent/50 hover:scale-105 transition-all duration-300 cursor-pointer transform-gpu">
                        <Typography variant="body2">Hover for scale effect</Typography>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10 hover:border-soft-violet/50 hover:rotate-2 transition-all duration-300 cursor-pointer transform-gpu">
                        <Typography variant="body2">Hover for rotation</Typography>
                      </div>
                    </div>
                  </Card3D>

                  {/* Input Interactions */}
                  <Card3D className="p-8" index={1}>
                    <Typography variant="h4" className="mb-6 text-glow-cyan">
                      Input Enchantment
                    </Typography>
                    <div className="space-y-4">
                      <Input
                        label="Magical Search"
                        placeholder="Type for magic..."
                        icon={<SearchIcon />}
                        iconPosition="left"
                        className="transition-all duration-500 focus-within:shadow-cyan-glow"
                      />
                      <Input
                        label="Glowing Input"
                        placeholder="Focus me!"
                        className="transition-all duration-500 focus-within:shadow-naia-glow"
                      />
                    </div>
                  </Card3D>

                  {/* Click Interactions */}
                  <Card3D className="p-8" index={2}>
                    <Typography variant="h4" className="mb-6 text-glow-multi">
                      Click Symphony
                    </Typography>
                    <div className="space-y-4">
                      <button 
                        onClick={handleExplosiveClick}
                        className="w-full p-4 bg-gradient-to-r from-naia-pink to-cyan-accent rounded-xl font-bold text-white transition-all duration-300 hover:scale-105 active:scale-95 transform-gpu"
                      >
                        Click for Explosion
                      </button>
                      <button 
                        onClick={() => createExplosion(mousePosition.x, mousePosition.y)}
                        className="w-full p-4 bg-white/10 border border-white/20 rounded-xl font-bold text-white transition-all duration-300 hover:bg-white/20 hover:border-white/40 active:bg-white/30"
                      >
                        Create Particles
                      </button>
                    </div>
                  </Card3D>
                </div>
              </div>
            )}

            {/* Beautiful Loading States */}
            {showSkeletons && (
              <div className="mt-20 space-y-8 animate-fade-in">
                <Typography variant="h3" className="text-center text-4xl font-bold text-glow-multi">
                  Beautiful Loading States
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <Card3D className="p-6" index={0}>
                    <SongCardSkeleton />
                  </Card3D>
                  <Card3D className="p-6" index={1}>
                    <AlbumCardSkeleton />
                  </Card3D>
                  <Card3D className="p-6" index={2}>
                    <ArtistCardSkeleton />
                  </Card3D>
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
      
      {/* Ultra Floating Action Button */}
      <button
        onClick={handleExplosiveClick}
        className="fixed bottom-8 right-8 w-20 h-20 bg-gradient-to-r from-naia-pink via-cyan-accent to-soft-violet rounded-full shadow-multi-glow hover:shadow-naia-intense transition-all duration-500 hover:scale-125 flex items-center justify-center text-white text-3xl animate-float z-50 group overflow-hidden transform-gpu"
      >
        <span className="relative z-10 transition-transform duration-300 group-hover:rotate-180">✨</span>
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent via-soft-violet to-naia-pink opacity-0 group-hover:opacity-100 transition-all duration-500 animate-gradient-x rounded-full"></div>
      </button>

      {/* Performance indicator */}
      <div className="fixed bottom-8 left-8 z-50">
        <Card3D className="p-4">
          <div className="text-center">
            <Typography variant="caption" color="secondary" className="block">
              FPS: 60 | Particles: {particles.length}
            </Typography>
            <Typography variant="caption" color="secondary" className="block">
              Beauty Level: {beautyScore.toFixed(1)}/10 🌟
            </Typography>
          </div>
        </Card3D>
      </div>
    </div>
  );
} 